import React from "react";
import styled from "styled-components";
import { Container, Section, Button } from "../components/ui";
import SEO from "../components/SEO";

const PageHeader = styled.div`
  text-align: center;
  padding: 4rem 0;
  background: #f9fafb;
`;

const PageTitle = styled.h1`
  margin-bottom: 1rem;
`;

const PageDesc = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  max-width: 600px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3rem 2rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  text-decoration: none;
  transition: all 250ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
  }
`;

const CardIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${({ $color }) => $color || '#fef3c7'};
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  img, svg {
    width: 40px;
    height: 40px;
  }
`;

const CardTitle = styled.h3`
  color: #0f0f0f;
  margin-bottom: 0.5rem;
`;

const CardDesc = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
`;

const SocialSection = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 3rem;
  border-top: 1px solid #e5e7eb;
`;

const SocialTitle = styled.h3`
  margin-bottom: 1.5rem;
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #f9fafb;
  border-radius: 0.5rem;
  color: #374151;
  font-weight: 500;
  text-decoration: none;
  transition: all 150ms ease;

  &:hover {
    background: #e5e7eb;
    color: #0f0f0f;
  }
`;

const Contact = () => {
  return (
    <>
      <SEO
        title="Contact"
        description="Get in touch with The New Calendar team. Join our community or email us directly."
        url="https://thenewcalendar.com/contact"
      />

      <PageHeader>
        <Container>
          <PageTitle>Get in Touch</PageTitle>
          <PageDesc>
            Have questions? Want to collaborate? We'd love to hear from you.
          </PageDesc>
        </Container>
      </PageHeader>

      <Section>
        <Container>
          <ContactGrid>
            <ContactCard href="mailto:tom@thenewcalendar.com">
              <CardIcon $color="#fef3c7">
                <svg viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <path d="M22 6l-10 7L2 6"/>
                </svg>
              </CardIcon>
              <CardTitle>Email Us</CardTitle>
              <CardDesc>tom@thenewcalendar.com</CardDesc>
            </ContactCard>

            <ContactCard href="https://discord.gg/dMtAET3gQu" target="_blank" rel="noopener noreferrer">
              <CardIcon $color="#e0e7ff">
                <svg viewBox="0 0 24 24" fill="#5865F2">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </CardIcon>
              <CardTitle>Join Discord</CardTitle>
              <CardDesc>Chat with the community</CardDesc>
            </ContactCard>
          </ContactGrid>

          <SocialSection>
            <SocialTitle>Follow Us</SocialTitle>
            <SocialLinks>
              <SocialLink href="https://tiktok.com/@thenewcalendar" target="_blank" rel="noopener noreferrer">
                📱 TikTok
              </SocialLink>
              <SocialLink href="https://instagram.com/thenewcalendar" target="_blank" rel="noopener noreferrer">
                📸 Instagram
              </SocialLink>
              <SocialLink href="https://twitter.com/thenewcalendar" target="_blank" rel="noopener noreferrer">
                🐦 Twitter
              </SocialLink>
              <SocialLink href="https://facebook.com/thenewcalendar" target="_blank" rel="noopener noreferrer">
                👍 Facebook
              </SocialLink>
            </SocialLinks>
          </SocialSection>
        </Container>
      </Section>
    </>
  );
};

export default Contact;
